/**
 * An overlay, which serves as a tap recognizer for touch gestures on mobile.
 * */

import { useAtom } from 'jotai'
import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import useAtomWithRef from '../../../../utils/useAtomWithRef'
import {
  rControlsVisibilityAtom,
  skipBackwardsSecondsAtom,
  skipForwardsSecondsAtom,
  videoElementAtom,
  wControlsVisibilityAtom,
} from '../atoms'
import TapRecognizerSegment from './TapRecognizerSegment'

//* =========================== React Component ===========================================

const TapRecognizer = (): JSX.Element => {
  // Signals if the user is currently skipping around.
  const [skipMode, setSkipMode] = useState(false)
  const skipModeInactiveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [videoElementRef] = useAtomWithRef(videoElementAtom)

  const [, setSkipForwardsSeconds] = useAtom(skipForwardsSecondsAtom)
  const [, setSkipBackwardsSeconds] = useAtom(skipBackwardsSecondsAtom)

  const [controlsVisible] = useAtom(rControlsVisibilityAtom)
  const [, setControlsVisible] = useAtom(wControlsVisibilityAtom)

  // This method activates the skip mode and starts a timer which deactivates it again.
  const activateSkipMode = useCallback(() => {
    // Start a timer which deactivates the skip mode after a certain time, when
    // there are no more taps.
    const skipModeInactiveTimerId = setTimeout(() => {
      setSkipMode(false)
      skipModeInactiveTimerRef.current = null

      // Reset skip counters.
      setSkipForwardsSeconds(0)
      setSkipBackwardsSeconds(0)
    }, 370)

    setSkipMode(true)
    skipModeInactiveTimerRef.current = skipModeInactiveTimerId
  }, [])

  // This method starts a new timer if and only if there is already antoher timer running.
  const restartTimerIfActive = useCallback(() => {
    if (skipModeInactiveTimerRef.current !== null) {
      // Cancel active timer.
      clearTimeout(skipModeInactiveTimerRef.current)
      // Start a new skip mode timer.
      activateSkipMode()
    }
  }, [])

  return (
    <>
      <Row>
        {/* The tap segment detecting skipping backwards. */}
        <TapRecognizerSegment
          flexGrow={3}
          onlySingleTap={skipMode}
          onSingleTap={() => {
            const videoElement = videoElementRef.current!
            // While the skip mode is enabled, every single tap renews
            // the timer.
            if (skipMode) {
              restartTimerIfActive()

              setSkipForwardsSeconds(0)
              setSkipBackwardsSeconds((prevState) => prevState + 10)

              videoElement.currentTime = videoElement.currentTime - 10
            } else {
              // Show the controls.
              setControlsVisible(!controlsVisible)
            }
          }}
          onDoubleTap={() => {
            const videoElement = videoElementRef.current!

            setSkipBackwardsSeconds((prevState) => prevState + 10)
            videoElement.currentTime = videoElement.currentTime - 10
            activateSkipMode()
          }}
        />
        {/* The tap segment detecting play/pause. */}
        <TapRecognizerSegment
          flexGrow={4}
          onlySingleTap={skipMode}
          onSingleTap={() => {
            // When in skip mode, a single tap in the mid segment doesn't do
            // anything.
            if (skipMode) return

            // Show the controls.
            setControlsVisible(!controlsVisible)
          }}
          onDoubleTap={() => {
            const videoElement = videoElementRef.current!

            videoElement.paused ? videoElement.play() : videoElement.pause()
          }}
        />
        {/* The tap segment detecting skipping forward. */}
        <TapRecognizerSegment
          flexGrow={3}
          onlySingleTap={skipMode}
          onSingleTap={() => {
            const videoElement = videoElementRef.current!
            // While the skip mode is enabled, every single tap renews
            // the timer.
            if (skipMode) {
              restartTimerIfActive()

              setSkipBackwardsSeconds(0)
              setSkipForwardsSeconds((prevState) => prevState + 10)

              videoElement.currentTime = videoElement.currentTime + 10
            } else {
              // Show the controls.
              setControlsVisible(!controlsVisible)
            }
          }}
          onDoubleTap={() => {
            const videoElement = videoElementRef.current!

            setSkipForwardsSeconds((prevState) => prevState + 10)
            videoElement.currentTime = videoElement.currentTime + 10
            activateSkipMode()
          }}
        />
      </Row>
    </>
  )
}

export default TapRecognizer

//* =========================== Styled Components ===========================================

const Row = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: row;
  pointer-events: all;
`
